import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    IconButton,
    Chip,
    Typography,
    Tooltip,
    CircularProgress,
    TableSortLabel,
    FormControlLabel,
    Checkbox,
    Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { getTournamentsWithOrganizers, updateTournament, deleteTournament } from '../../firebase/firestore';
import { TOURNAMENT_RANKS } from '../Calendar/RankFilter';

const TournamentList = ({ onEdit, onTournamentUpdated }) => {
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredTournaments, setFilteredTournaments] = useState([]);
    const [showUpcoming, setShowUpcoming] = useState(false);
    const [orderBy, setOrderBy] = useState('date');
    const [order, setOrder] = useState('asc');

    const fetchTournaments = async () => {
        try {
            setLoading(true);
            const data = await getTournamentsWithOrganizers();
            setTournaments(data);
            setFilteredTournaments(data);
        } catch (error) {
            console.error('Błąd podczas pobierania turniejów:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTournaments();
    }, []);

    useEffect(() => {
        if (onTournamentUpdated) {
            fetchTournaments();
        }
    }, [onTournamentUpdated]);

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortTournaments = (tournaments) => {
        return [...tournaments].sort((a, b) => {
            if (orderBy === 'date') {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return order === 'asc' ? dateA - dateB : dateB - dateA;
            }
            return 0;
        });
    };

    useEffect(() => {
        let filtered = tournaments.filter(tournament => {
            const searchLower = searchQuery.toLowerCase();
            const matchesSearch =
                tournament.name?.toLowerCase().includes(searchLower) ||
                tournament.city?.toLowerCase().includes(searchLower) ||
                tournament.organizer?.name?.toLowerCase().includes(searchLower);

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tournamentDate = new Date(tournament.date);
            tournamentDate.setHours(0, 0, 0, 0);

            const matchesUpcoming = showUpcoming ? tournamentDate >= today : true;

            return matchesSearch && matchesUpcoming;
        });

        filtered = sortTournaments(filtered);
        setFilteredTournaments(filtered);
        setPage(0);
    }, [searchQuery, tournaments, showUpcoming, orderBy, order]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDelete = async (tournamentId) => {
        if (window.confirm('Czy na pewno chcesz usunąć ten turniej?')) {
            try {
                await deleteTournament(tournamentId);
                setTournaments(prev => prev.filter(t => t.id !== tournamentId));
            } catch (error) {
                console.error('Błąd podczas usuwania turnieju:', error);
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Kompaktowe filtry */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                <TextField
                    placeholder="Szukaj turnieju..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: 'rgb(156, 163, 175)', mr: 1, fontSize: '1.2rem' }} />,
                    }}
                    sx={{
                        flex: { xs: '1 1 100%', sm: '1 1 auto' },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(76, 29, 149, 0.08)',
                            '& fieldset': {
                                borderColor: 'rgba(76, 29, 149, 0.2)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(76, 29, 149, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'rgb(196, 181, 253)',
                            },
                        },
                        '& .MuiInputBase-input': {
                            color: 'white',
                            fontSize: '0.875rem',
                        },
                    }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showUpcoming}
                            onChange={(e) => setShowUpcoming(e.target.checked)}
                            size="small"
                            sx={{
                                color: 'rgb(196, 181, 253)',
                                '&.Mui-checked': {
                                    color: 'rgb(196, 181, 253)',
                                },
                            }}
                        />
                    }
                    label="Nadchodzące"
                    sx={{
                        color: 'white',
                        '& .MuiFormControlLabel-label': {
                            color: 'white',
                            fontSize: '0.875rem'
                        },
                    }}
                />
            </Stack>

            {/* Kompaktowa tabela */}
            <TableContainer component={Paper} sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                borderRadius: '0.5rem',
                border: '1px solid rgb(55, 65, 81)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                overflowX: 'auto'
            }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(76, 29, 149, 0.1)' }}>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Nazwa</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Data</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Miasto</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Organizator</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Ranga</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Format</TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Akcje</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTournaments
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((tournament) => (
                                <TableRow
                                    key={tournament.id}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.08)',
                                        },
                                        transition: 'background-color 0.2s'
                                    }}
                                >
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{tournament.name}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'nowrap'
                                    }}>{new Date(tournament.date).toLocaleDateString('pl-PL')}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem'
                                    }}>{tournament.city}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{tournament.organizer?.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={tournament.rank || 'Nieokreślona'}
                                            size="small"
                                            sx={{
                                                backgroundColor: tournament.rank ? `${TOURNAMENT_RANKS[tournament.rank].color}20` : 'rgba(76, 29, 149, 0.3)',
                                                color: tournament.rank ? TOURNAMENT_RANKS[tournament.rank].color : 'rgb(196, 181, 253)',
                                                border: tournament.rank ? `1px solid ${TOURNAMENT_RANKS[tournament.rank].color}` : 'none',
                                                fontSize: '0.65rem',
                                                height: '20px'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem'
                                    }}>{tournament.format || 'Nieokreślony'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Edytuj">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(tournament)}
                                                    sx={{
                                                        color: 'rgb(196, 181, 253)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Usuń">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(tournament.id)}
                                                    sx={{
                                                        color: '#ef4444',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredTournaments.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 15, 25, 50]}
                    sx={{
                        color: 'rgb(196, 181, 253)',
                        '.MuiTablePagination-select': {
                            color: 'rgb(196, 181, 253)',
                        },
                        '.MuiTablePagination-selectIcon': {
                            color: 'rgb(196, 181, 253)',
                        },
                        '.MuiIconButton-root': {
                            color: 'rgb(196, 181, 253)',
                            '&:hover': {
                                backgroundColor: 'rgba(196, 181, 253, 0.1)',
                            },
                            '&.Mui-disabled': {
                                color: 'rgb(75, 85, 99)',
                            }
                        }
                    }}
                />
            </TableContainer>
        </Box>
    );
};

export default TournamentList; 